import { collection, query, where, getDocs, orderBy, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { searchPatients } from '../firebase';
import { getAllForms } from '../firebase/formManagement';

// Cache for user names to avoid repeated queries
const userNameCache = new Map<string, string>();

/**
 * Get user name by ID or email
 */
async function getUserName(userIdOrEmail: string): Promise<string> {
  if (!userIdOrEmail) return 'Unknown';
  
  // Check cache first
  if (userNameCache.has(userIdOrEmail)) {
    return userNameCache.get(userIdOrEmail)!;
  }
  
  try {
    // If it's already an email, try to find user by email
    if (userIdOrEmail.includes('@')) {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        if (userData.email === userIdOrEmail || userData.linkedGoogleEmail === userIdOrEmail) {
          const name = userData.name || userData.email?.split('@')[0] || userIdOrEmail;
          userNameCache.set(userIdOrEmail, name);
          return name;
        }
      }
      // If not found, use email prefix as name
      const emailName = userIdOrEmail.split('@')[0];
      userNameCache.set(userIdOrEmail, emailName);
      return emailName;
    }
    
    // Try to get user by ID
    const userRef = doc(db, 'users', userIdOrEmail);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const name = userData.name || userData.email?.split('@')[0] || userIdOrEmail;
      userNameCache.set(userIdOrEmail, name);
      return name;
    }
    
    // Fallback: return as-is if not found
    userNameCache.set(userIdOrEmail, userIdOrEmail);
    return userIdOrEmail;
  } catch (error) {
    console.error('Error getting user name:', error);
    userNameCache.set(userIdOrEmail, userIdOrEmail);
    return userIdOrEmail;
  }
}

export interface SearchResult {
  type: 'patient' | 'event' | 'task' | 'form' | 'milestone';
  id: string;
  title: string;
  subtitle?: string;
  caseId?: string;
  data: any;
}

/**
 * Global search across all database collections
 * Searches: patients, events (meetings), tasks, intake forms, milestones
 */
export async function globalSearch(searchTerm: string): Promise<SearchResult[]> {
  if (!searchTerm || searchTerm.trim().length < 2) {
    return [];
  }

  const term = searchTerm.trim().toLowerCase();
  const results: SearchResult[] = [];

  try {
    // Search all collections in parallel
    const [patientResults, eventResults, taskResults, formResults, milestoneResults] = await Promise.all([
      searchPatientsData(term),
      searchEvents(term),
      searchTasks(term),
      searchForms(term),
      searchMilestones(term)
    ]);

    results.push(...patientResults, ...eventResults, ...taskResults, ...formResults, ...milestoneResults);

    // Sort by relevance (exact matches first, then partial matches)
    return results.sort((a, b) => {
      const aExact = a.title.toLowerCase() === term || a.title.toLowerCase().startsWith(term);
      const bExact = b.title.toLowerCase() === term || b.title.toLowerCase().startsWith(term);
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      return a.title.localeCompare(b.title);
    });
  } catch (error) {
    console.error('Error in global search:', error);
    return [];
  }
}

/**
 * Search patients by name, email, or case ID
 */
async function searchPatientsData(term: string): Promise<SearchResult[]> {
  try {
    // Search via PostgreSQL API
    const patients = await searchPatients(term);
    
    console.log('=== PATIENT SEARCH DEBUG ===');
    console.log('Search term:', term);
    console.log('Number of patients found:', patients.length);
    if (patients.length > 0) {
      console.log('First patient data:', JSON.stringify(patients[0], null, 2));
      console.log('First patient keys:', Object.keys(patients[0]));
    }
    console.log('=== END DEBUG ===');
    
    return patients.map((patient, index) => {
      // Try different possible field names for names (camelCase, snake_case, etc.)
      const firstName = patient.firstName || patient.first_name || patient.firstName_he || '';
      const lastName = patient.lastName || patient.last_name || patient.lastName_he || '';
      
      console.log(`Patient ${index}:`, {
        firstName,
        lastName,
        email: patient.email,
        caseId: patient.caseId || patient.case_id,
        allKeys: Object.keys(patient)
      });
      
      // Combine names
      const nameParts = [firstName, lastName].filter(Boolean);
      const fullName = nameParts.join(' ').trim();
      
      // Try to get display name from various sources
      let displayName = fullName;
      if (!displayName) {
        displayName = patient.name || patient.full_name || patient.fullName || '';
      }
      if (!displayName) {
        displayName = patient.email || '';
      }
      
      // If still no name, use case ID instead of "Unknown Patient"
      const caseId = patient.caseId || patient.case_id;
      if (!displayName && caseId) {
        displayName = caseId;
      }
      
      // Only show "Unknown Patient" as last resort
      if (!displayName) {
        displayName = 'Unknown Patient';
      }
      
      console.log(`Final displayName for patient ${index}:`, displayName);
      
      return {
        type: 'patient' as const,
        id: caseId || patient.id,
        title: displayName,
        subtitle: caseId || '',
        caseId: caseId,
        data: patient
      };
    });
  } catch (error) {
    console.error('Error searching patients:', error);
    return [];
  }
}

/**
 * Search events (meetings) by title, description, or case ID
 */
async function searchEvents(term: string): Promise<SearchResult[]> {
  try {
    const eventsRef = collection(db, 'events');
    const snapshot = await getDocs(eventsRef);
    
    const results: SearchResult[] = [];
    
    // First pass: collect all matching events
    const events: Array<{doc: any, data: any, createdBy?: string}> = [];
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const title = data.title || '';
      const description = data.description || '';
      const caseId = data.caseId || '';
      
      // Search in title, description, or case ID
      if (
        title.toLowerCase().includes(term) ||
        description.toLowerCase().includes(term) ||
        caseId.toLowerCase().includes(term)
      ) {
        events.push({ doc, data, createdBy: data.createdBy });
      }
    });
    
    // Get unique user IDs and fetch their names
    const userIds = events.map(e => e.createdBy).filter((id): id is string => Boolean(id));
    const uniqueUserIds = Array.from(new Set(userIds));
    const userNamePromises = uniqueUserIds.map(userId => getUserName(userId));
    const userNames = await Promise.all(userNamePromises);
    const userMap = new Map<string, string>();
    uniqueUserIds.forEach((userId, index) => {
      userMap.set(userId, userNames[index]);
    });
    
    // Second pass: create results with user names
    events.forEach(({ doc, data, createdBy }) => {
      const createdByName = createdBy ? userMap.get(createdBy) : '';
      const subtitleParts = [
        createdByName ? `By: ${createdByName}` : '',
        data.date ? new Date(data.date.toDate ? data.date.toDate() : data.date).toLocaleDateString() : ''
      ].filter(Boolean);
      
      results.push({
        type: 'event',
        id: doc.id,
        title: data.title || 'Untitled Event',
        subtitle: subtitleParts.join(' | ') || undefined,
        caseId: data.caseId || '',
        data: { ...data, id: doc.id }
      });
    });
    
    return results;
  } catch (error) {
    console.error('Error searching events:', error);
    return [];
  }
}

/**
 * Search tasks by title, description, or case ID
 */
async function searchTasks(term: string): Promise<SearchResult[]> {
  try {
    const tasksRef = collection(db, 'tasks');
    const snapshot = await getDocs(query(tasksRef, orderBy('createdAt', 'desc')));
    
    const results: SearchResult[] = [];
    
    // First pass: collect all matching tasks
    const tasks: Array<{doc: any, data: any, createdBy?: string}> = [];
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const title = data.title || '';
      const description = data.description || '';
      const caseId = data.caseId || '';
      const patientName = data.patientName || '';
      
      // Search in title, description, case ID, or patient name
      if (
        title.toLowerCase().includes(term) ||
        description.toLowerCase().includes(term) ||
        caseId.toLowerCase().includes(term) ||
        patientName.toLowerCase().includes(term)
      ) {
        tasks.push({ doc, data, createdBy: data.createdBy });
      }
    });
    
    // Get unique user IDs and fetch their names
    const userIds = tasks.map(t => t.createdBy).filter((id): id is string => Boolean(id));
    const uniqueUserIds = Array.from(new Set(userIds));
    const userNamePromises = uniqueUserIds.map(userId => getUserName(userId));
    const userNames = await Promise.all(userNamePromises);
    const userMap = new Map<string, string>();
    uniqueUserIds.forEach((userId, index) => {
      userMap.set(userId, userNames[index]);
    });
    
    // Second pass: create results with user names
    tasks.forEach(({ doc, data, createdBy }) => {
      const createdByName = createdBy ? userMap.get(createdBy) : '';
      const subtitleParts = [
        data.patientName || '',
        createdByName ? `By: ${createdByName}` : '',
        `Status: ${data.status || 'pending'}`
      ].filter(Boolean);
      
      results.push({
        type: 'task',
        id: doc.id,
        title: data.title || 'Untitled Task',
        subtitle: subtitleParts.join(' | ') || undefined,
        caseId: data.caseId || '',
        data: { ...data, id: doc.id }
      });
    });
    
    return results;
  } catch (error) {
    console.error('Error searching tasks:', error);
    return [];
  }
}

/**
 * Search intake forms by name (English or Hebrew)
 */
async function searchForms(term: string): Promise<SearchResult[]> {
  try {
    const forms = await getAllForms();
    
    return forms
      .filter(form => {
        const nameEn = (form.nameEn || form.name || '').toLowerCase();
        const nameHe = (form.nameHe || form.name || '').toLowerCase();
        const description = (form.description || '').toLowerCase();
        
        return nameEn.includes(term) || nameHe.includes(term) || description.includes(term);
      })
      .map(form => ({
        type: 'form' as const,
        id: form.id,
        title: form.nameEn || form.nameHe || form.name || 'Untitled Form',
        subtitle: form.description || `Type: ${form.type || 'custom'}`,
        data: form
      }));
  } catch (error) {
    console.error('Error searching forms:', error);
    return [];
  }
}

/**
 * Search milestones by title, description, or case ID
 */
async function searchMilestones(term: string): Promise<SearchResult[]> {
  try {
    const milestonesRef = collection(db, 'milestones');
    const snapshot = await getDocs(query(milestonesRef, orderBy('dueDate', 'desc')));
    
    const results: SearchResult[] = [];
    
    // First pass: collect all matching milestones
    const milestones: Array<{doc: any, data: any, createdBy?: string}> = [];
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const title = data.title || '';
      const description = data.description || '';
      const caseId = data.caseId || '';
      
      // Search in title, description, or case ID
      if (
        title.toLowerCase().includes(term) ||
        description.toLowerCase().includes(term) ||
        caseId.toLowerCase().includes(term)
      ) {
        milestones.push({ doc, data, createdBy: data.createdBy });
      }
    });
    
    // Get unique user IDs and fetch their names
    const userIds = milestones.map(m => m.createdBy).filter((id): id is string => Boolean(id));
    const uniqueUserIds = Array.from(new Set(userIds));
    const userNamePromises = uniqueUserIds.map(userId => getUserName(userId));
    const userNames = await Promise.all(userNamePromises);
    const userMap = new Map<string, string>();
    uniqueUserIds.forEach((userId, index) => {
      userMap.set(userId, userNames[index]);
    });
    
    // Second pass: create results with user names
    milestones.forEach(({ doc, data, createdBy }) => {
      const createdByName = createdBy ? userMap.get(createdBy) : '';
      const subtitleParts = [
        createdByName ? `By: ${createdByName}` : '',
        `Status: ${data.status || 'pending'}`
      ].filter(Boolean);
      
      results.push({
        type: 'milestone',
        id: doc.id,
        title: data.title || 'Untitled Milestone',
        subtitle: subtitleParts.join(' | ') || undefined,
        caseId: data.caseId || '',
        data: { ...data, id: doc.id }
      });
    });
    
    return results;
  } catch (error) {
    console.error('Error searching milestones:', error);
    return [];
  }
}

